import { memo, useCallback, useMemo } from 'react'
import { Image, Table, Space, Popconfirm } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { League } from 'types/leagues'
import { LEAGUE_FORMAT_STAGES_ID } from 'types/league-formats'
import { ExpandableRow } from './expandable-row'
import { modalTitle as formatModalTitle } from 'utils/modal-title'
import { useNavigate } from 'react-router-dom'
import { LockTwoTone } from '@ant-design/icons'

type Props = {
    data: League[]
    onEdit: (id: number) => void
    onDelete: (id: number) => void
    onAddTeam: (id: number) => void
    onDeleteTeam: (teamId: number, leagueId: number) => void
    loading: boolean
    onShowScoreTable: (leaguesId: number) => void
    onShowScorerTable: (leaguesId: number) => void
}

export const LeaguesTable = memo((props: Props) => {
    const { data, onEdit, onDelete, onAddTeam, onDeleteTeam, loading, onShowScoreTable, onShowScorerTable } = props
    const navigate = useNavigate()

    const goToGames = useCallback(
        (leagueId: number) => {
            navigate(`${leagueId}/games`)
        },
        [navigate]
    )

    const goToBracket = useCallback(
        (leagueId: number) => {
            navigate(`${leagueId}/bracket`)
        },
        [navigate]
    )

    const columns: ColumnsType<League> = useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: '',
                key: 'logo-preview',
                render: (_, record) =>
                    record.logo ? <Image height={40} width={40} src={record.logo} preview={false} /> : '—',
            },
            {
                title: 'Name',
                key: 'name',
                render: (_, record) => {
                    return (
                        <>
                            {record.isFinished && <LockTwoTone twoToneColor='#eb2f96' />}
                            {record.name}
                        </>
                    )
                },
            },
            {
                title: 'Short name',
                dataIndex: 'short_name',
                key: 'short_name',
            },
            {
                title: 'Logo',
                dataIndex: 'logo',
                key: 'logo',
                render: (value: string | undefined) => value ?? '—',
            },
            {
                title: 'League type',
                key: 'leagueTypeId',
                render: (_, record) => {
                    return record.leagueType?.name
                },
            },
            {
                title: 'League division',
                key: 'leagueDivisionId',
                render: (_, record) => {
                    return record.leagueDivision?.name
                },
            },
            {
                title: 'League format',
                key: 'leagueFormatId',
                render: (_, record) => {
                    return record.leagueFormat?.name
                },
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                    <Space size='middle'>
                        <a onClick={() => onEdit(record.id)}>Edit</a>
                        <Popconfirm
                            title={formatModalTitle('Delete league', record.name)}
                            description={'Are you sure to delete this league?'}
                            onConfirm={() => onDelete(record.id)}
                            okText={'Yes'}
                            cancelText={'No'}
                        >
                            <a href='#'>Delete</a>
                        </Popconfirm>
                        <a onClick={() => onAddTeam(record.id)}>Add team</a>
                        <a onClick={() => goToGames(record.id)}>Games</a>
                        <a onClick={() => onShowScoreTable(record.id)}>Score table</a>
                        <a onClick={() => onShowScorerTable(record.id)}>Scorer table</a>
                        {record.leagueFormatId === LEAGUE_FORMAT_STAGES_ID && (
                            <a onClick={() => goToBracket(record.id)}>Bracket</a>
                        )}
                    </Space>
                ),
            },
        ],
        [goToBracket, goToGames, onAddTeam, onDelete, onEdit, onShowScoreTable, onShowScorerTable]
    )

    return (
        <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey='id'
            pagination={false}
            expandable={{
                expandedRowRender: record => {
                    return (
                        <ExpandableRow
                            data={record.teamsToLeagues}
                            leagueName={record.name}
                            onDeleteTeam={onDeleteTeam}
                        />
                    )
                },
                rowExpandable: record => !!record.teamsToLeagues.length,
            }}
        />
    )
})
